import { useEffect } from 'react';
import { PhoneCall, Mail, MapPin } from 'lucide-react';
import Head from 'next/head';
import Header from '../components/Header';
import Hero from '../components/Hero';
import ContactUs from '../components/ContactUs';
import Footer from '../components/Footer';
import QuickQuoteForm from '../components/QuickQuoteForm';
import { generateWebPageSchema } from '../utils/generateWebPageSchema';

export default function ContactUsPage() {
  useEffect(() => {
    // Only run on client side
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const base = 'https://modernbuildremodel.com';
  const pageUrl = `${base}/contact`;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": base },
      { "@type": "ListItem", "position": 2, "name": "Contact Us", "item": pageUrl }
    ]
  };

  return (
    <div className="bg-white">
      <>
        <Head>
          <title>Get in Touch with Our Remodeling Experts | Modern Build</title>
          <meta name="title" content="Get in Touch with Our Remodeling Experts | Modern Build" />
          <meta name="description" content="Reach out to Modern Build for expert remodeling services in Los Angeles. Call us or fill out the contact form to schedule your free consultation." />
          <meta name="keywords" content="Contact Modern Build, remodeling contact, Los Angeles contractors, home renovation contact, free estimate remodeling" />
          <meta name="author" content="Modern Build Home Remodeling" />

          {/* Canonical URL to avoid duplicate content issues */}
          <link rel="canonical" href="https://modernbuildremodel.com/contact" />

          {/* Open Graph Meta Tags for social sharing */}
          <meta property="og:title" content="Get in Touch with Our Remodeling Experts | Modern Build" />
          <meta property="og:description" content="Talk to the remodeling pros. Let’s plan your dream home today. Contact Modern Build for expert renovations, design ideas, and free consultations in Los Angeles." />
          <meta property="og:image" content="https://modernbuildremodel.com/hero/best-expert-home-remodeling-services-in-los-angeles.webp" />
          <meta property="og:url" content="https://modernbuildremodel.com/contact" />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="Modern Build Home Remodeling" />

          {/* Twitter Card Meta Tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Get in Touch with Our Remodeling Experts | Modern Build" />
          <meta name="twitter:description" content="Have questions? Ready to remodel? Call or message Modern Build to connect with Los Angeles’s top remodeling pros and schedule your free consultation today." />
          <meta name="twitter:image" content="https://modernbuildremodel.com/hero/best-expert-home-remodeling-services-in-los-angeles.webp" />
          <meta name="twitter:site" content="@modernbuildhome" />
       
          {/* JSON-LD: WebPage */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(
                generateWebPageSchema({
                  title: 'Contact Modern Build | Book Free Consultation',
                  description: 'Get in touch with Modern Build for free home remodeling consultations, quotes, and inquiries in the LA area.',
                  slug: '/contact'
                })
              )
            }}
          />

          {/* JSON-LD: Breadcrumb */}
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />


        </Head>

      <Header />
        <Hero category="contact" page="page" hideButton={true} hasContentH1={false}/>
        <QuickQuoteForm/>
        {/* Contact Info + Map */}
        <section className="bg-white py-20 px-4 text-text">
          <div className="max-w-[1140px] mx-auto flex flex-col lg:flex-row items-start justify-between gap-12">
            {/* Info Section */}
            <div className="lg:w-1/2 space-y-6">
              <h2 className="text-3xl font-gilda font-light text-primary">Get in Touch</h2>
              <p className="text-gray font-light">
                Whether you're ready to start your remodeling project or just have a few questions, our team is here to help.
                Contact us via phone, email, or visit our office.
              </p>

              <div className="space-y-6 text-gray">
                {/* Phone */}
                <div className="flex items-start gap-4">
                  <PhoneCall className="text-primary font-light w-10 h-10 mt-3 mt-1" />
                  <div>
                    <p className="font-light text-lg">Phone</p>
                    <a href="tel:+13233004130" className="text-primary text-2xl hover:underline">
                      (323) 300-4130
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <Mail className="text-primary font-light w-10 h-10 mt-3 mt-1" />
                  <div>
                    <p className="font-light text-lg">Email</p>
                    <a href="mailto:remodel@modernbuildremodel.com" className="text-primary text-2xl hover:underline">
                      remodel@modernbuildremodel.com
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-4">
                  <MapPin className="text-primary font-light w-10 h-10 mt-3 mt-1" />
                  <div>
                    <p className="font-light text-lg">Address</p>
                    <p className="font-light text-lg">
                      4222 Longridge Ave, Studio City, CA 91604
                      <br />
                      United States of America
                    </p>
                  </div>
                </div>
              </div>
            </div>


            {/* Map Section */}
            <div className="lg:w-1/2 w-full h-[400px] rounded-lg overflow-hidden shadow-md">
              <iframe
                title="Modern Build Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d55784.5389701682!2d-118.43569847300745!3d34.14545397465628!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c29620bce88ae5%3A0x4e60d2eb7952607!2s4222%20Longridge%20Ave%2C%20Studio%20City%2C%20CA%2091604!5e0!3m2!1sen!2sus!4v1746987782318!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </section>


        {/* Contact Form */}
        <ContactUs />
        <Footer />
      </>
    </div>
  );
}